import React, { useEffect } from "react";
import {
  fetchTransactionNotification,
  transactionCleanData,
} from "reducers/transactionSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { Link } from "react-router-dom";
import { getDiffTime, UTCToJapanTime } from "common/helper/time.helper";
import { Transaction } from "common/interfaces";
import { transactionNotificationText } from "common/helper/text.helper";
import { message, Table, TableColumnsType } from "antd";
import PaginateListMobile from "components/PaginateListMobile";
import { usePagination } from "common/hooks/usePagination";
import { parseErrorMessage } from "common/utils";
import { useGetTransactionsQuery } from "reducers/transactionApi";
import TransactionItem from "components/TransactionItem";
import {
  TransactionStatusEnum,
  TransactionStatusLabel,
} from "common/enums/TransactionStatus.enum";
import { isEmpty } from "lodash";
import { useCommonContext } from "contexts/CommonContext";
import { IconKeyboardArrowRightBlue } from "assets/svg";

const columns: TableColumnsType<Transaction> = [
  {
    title: "取引ID",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Transaction) => (
      <Link
        to={`/seller/product/${record.product.code}/transaction/${record.buyer_id}/${record.seller_id}`}
        className="text-primary"
      >
        #{record.code}
      </Link>
    ),
  },
  {
    title: "出品ID",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Transaction) => (
      <Link
        to={`/seller/product/${record.product.code}`}
        className="text-primary"
      >
        #{record.product.code}
      </Link>
    ),
  },
  {
    title: "商品名",
    dataIndex: "product",
    align: "center",
    render: (value) => value.product_name,
  },
  {
    title: "買い手企業",
    dataIndex: "buyer",
    align: "center",
    render: (value) => value.company_name,
  },
  {
    title: "ステータス",
    dataIndex: "status",
    align: "center",
    render: (value: TransactionStatusEnum) => TransactionStatusLabel[value],
  },
  {
    title: "最終更新",
    dataIndex: "created_at",
    align: "center",
    render: (value) => {
      const timeJapan = UTCToJapanTime(value);
      return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
    },
  },
  {
    title: "取引画面",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Transaction) => (
      <>
        <Link
          to={`/seller/product/${record.product.code}/transaction/${record.buyer_id}/${record.seller_id}`}
          className="text-primary"
        >
          取引画面へ
        </Link>
      </>
    ),
  },
];

const TransactionStatus = () => {
  const { setHeaderTitle } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const { transactionNotificationData } = useSelector(
    (state: RootState) => state.transaction,
  );

  const {
    pagination,
    paginationMobile,
    onChangePage,
    updateTotal,
    updateTotalMobile,
  } = usePagination();

  const { data: transactionData, error: inquiryErrClose } =
    useGetTransactionsQuery({
      page: pagination.current,
    });

  const { data: transactionMobileData, error: inquiryMobileErrClose } =
    useGetTransactionsQuery({
      page: paginationMobile.current,
    });

  useEffect(() => {
    if (transactionData?.data?.meta?.total_items) {
      updateTotal(transactionData.data.meta.total_items);
    }
  }, [transactionData]);

  useEffect(() => {
    if (transactionMobileData?.data?.meta?.total_items) {
      updateTotalMobile(transactionMobileData.data.meta.total_items);
    }
  }, [transactionMobileData]);

  useEffect(() => {
    if (inquiryErrClose) void message.error(parseErrorMessage(inquiryErrClose));
  }, [inquiryErrClose]);

  useEffect(() => {
    if (inquiryMobileErrClose)
      void message.error(parseErrorMessage(inquiryMobileErrClose));
  }, [inquiryMobileErrClose]);

  useEffect(() => {
    dispatch(fetchTransactionNotification());
    setHeaderTitle("取引ステータスの管理");
    return () => {
      dispatch(transactionCleanData());
    };
  }, []);

  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-10 overflow-hidden">
      <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
        <div className="self-stretch flex flex-col justify-start items-start">
          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-primary rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-primary text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-9">
                  取引についてのお知らせ
                </div>
              </div>
            </div>
          </div>
          {/*Contents*/}
          <div className="flex flex-col justify-center items-center gap-[5px] self-stretch">
            <div className="flex flex-col items-start self-stretch">
              {/*Items*/}
              {transactionNotificationData?.data.map((transaction, index) => {
                return (
                  <Link
                    to={`/seller/product/${transaction.product.code}/transaction/${transaction.buyer_id}/${transaction.seller_id}`}
                    key={index}
                    className="h-[89px] md:h-[42px] flex py-[10px] justify-center items-center gap-[10px] self-stretch border-b border-neutral-border bg-white"
                  >
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-[7px] md:gap-[10px] flex-[1_0_0]">
                      <div className="max-h-[44px] md:max-h-[22px] md:h-[22px] flex-[1_0_0] text-black font-['Noto Sans JP'] text-[14px] not-italic font-normal leading-[160%]">
                        {transactionNotificationText(transaction)}
                      </div>
                      <div className="h-[18px] md:h-[19px] text-primary text-left font-['Noto Sans JP'] text-[11px] md:text-[12px] not-italic font-normal leading-[160%]">
                        {getDiffTime(transaction.commonDate)}
                      </div>
                    </div>
                    <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Table<Transaction>
        className="w-full hidden md:block"
        rowKey="code"
        onChange={onChangePage}
        pagination={!isEmpty(transactionData?.data?.items) ? pagination : false}
        columns={columns}
        dataSource={transactionData?.data?.items || []}
      />
      <PaginateListMobile<Transaction>
        pagination={
          !isEmpty(transactionMobileData?.data?.items)
            ? paginationMobile
            : false
        }
        dataSource={transactionMobileData?.data?.items || []}
        renderItem={(item, checked, onSelect) => (
          <TransactionItem<Transaction>
            transaction={item}
            checked={checked}
            onSelect={onSelect}
            hideSelect={true}
          />
        )}
      />
    </div>
  );
};

export default TransactionStatus;
